import type { Conversation, Paginator, TwilioMessage } from '@twilio/conversations';
import { queryKey } from 'app/modules/live-chat/hooks/useTwilioMessages';
import { InfiniteData } from '@tanstack/react-query';
import { clearConnectingMessage, setConnectingMessage } from 'app/modules/live-chat/hooks/useConnectingMessage';

import { getTwilioConversation } from './conversation';
import queryClient from '../queryClient';
import { findLastConcierge, isConcierge, setConcierge } from './concierge';

export const enum MessageType {
  Disclaimer = 'disclaimer_message',
  QueTimeStarted = 'new_chat_nurses_notified',
  ChatArchived = 'conversation_archived',
  NNJoined = 'nurse_profile_info_v2',
}

const SPECIAL_MESSAGE_TYPES = [MessageType.ChatArchived];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isVisibleMessage = (message: any) => {
  const attr = message?.attributes;
  return !attr?.hidden || (attr?.concierge && attr?.hidden) || SPECIAL_MESSAGE_TYPES.includes(attr?.type);
};

const isConnectingMessage = (message: TwilioMessage): boolean => {
  return message.attributes?.type === MessageType.QueTimeStarted;
};

function listenForNewMessage(conversation: Conversation) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  conversation.on('messageAdded', (message: TwilioMessage) => {
    if (isConnectingMessage(message)) {
      setConnectingMessage(message);
    }

    if (!isVisibleMessage(message)) return;

    const isConciergeMessage = isConcierge(message);

    if (isConciergeMessage) {
      setConcierge(message.attributes?.concierge);
      clearConnectingMessage();
    }

    queryClient.setQueryData(
      queryKey,
      ({ pages, pageParams }: InfiniteData<{ hasPrevious: boolean; items: TwilioMessage[] }>) => ({
        pages: pages.map((page, index) => {
          if (pages.length === index + 1) {
            return {
              ...page,
              items: page.items.concat(message),
            };
          }
          return page;
        }),
        pageParams,
      })
    );
  });

  conversation.on('participantJoined', () => {
    clearConnectingMessage();
  });

  conversation.getParticipantsCount().then((count) => {
    if (count >= 2) {
      clearConnectingMessage();
    }
  });
}

let paginator: Paginator<TwilioMessage>;
async function getPaginator() {
  if (paginator) {
    return paginator;
  }
  const conversation = await getTwilioConversation();
  listenForNewMessage(conversation);

  return conversation.getMessages() as Promise<Paginator<TwilioMessage>>;
}
function setPaginator(p: Paginator<TwilioMessage>) {
  paginator = p;
}

export default async function fetchTwilioMessages(pageParam: number) {
  const paginator = await getPaginator();

  if (pageParam) {
    const prevPaginator = await paginator.prevPage();
    setPaginator(prevPaginator);
    return {
      hasPrevious: prevPaginator.hasPrevPage,
      items: prevPaginator.items.filter(isVisibleMessage),
    };
  }

  const connectingMessage = paginator.items.find(isConnectingMessage);
  if (connectingMessage) {
    setConnectingMessage(connectingMessage);
  }

  const concierge = findLastConcierge(paginator.items);
  if (concierge) {
    setConcierge(concierge);
    clearConnectingMessage();
  }

  return {
    hasPrevious: paginator.hasPrevPage,
    items: paginator.items.filter(isVisibleMessage),
  };
}
